import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { NavmenuService } from './shared/services/navmenu.service';

export const authGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const token = localStorage.getItem('token');

  if (!token) {
    router.navigate(['auth/login']);
    return false;
  }

  const expirySecondsStr = localStorage.getItem('loginSession');
  const expirySeconds = expirySecondsStr ? parseInt(expirySecondsStr, 10) : 0;

  // Only enforce calculated expiration if loginSession is a valid positive duration
  if (!isNaN(expirySeconds) && expirySeconds > 0) {
    const storedExpiry = localStorage.getItem('calculatedExpiry') || sessionStorage.getItem('calculatedExpiry');
    const now = new Date();

    if (storedExpiry) {
      const expiryDate = new Date(storedExpiry);
      if (!isNaN(expiryDate.getTime())) {
        if (now > expiryDate) {
          // Double check against loginTimestamp in case storedExpiry was stale
          const loginTimestampStr = localStorage.getItem('loginTimestamp');
          if (loginTimestampStr) {
            const loginDate = new Date(loginTimestampStr);
            if (!isNaN(loginDate.getTime())) {
              const freshExpiry = new Date(loginDate.getTime() + expirySeconds * 1000);
              if (now <= freshExpiry) {
                localStorage.setItem('calculatedExpiry', freshExpiry.toISOString());
                sessionStorage.setItem('calculatedExpiry', freshExpiry.toISOString());
                return true;
              }
            }
          }

          // Session genuinely expired
          localStorage.removeItem('token');
          localStorage.removeItem('calculatedExpiry');
          sessionStorage.removeItem('calculatedExpiry');
          localStorage.removeItem('loginTimestamp');
          router.navigate(['auth/login']);
          return false;
        }
        return true;
      }
    }

    // If not stored or invalid, calculate and store expiry based on loginTimestamp + expiry time
    const loginTimestampStr = localStorage.getItem('loginTimestamp');
    if (loginTimestampStr) {
      const loginDate = new Date(loginTimestampStr);
      if (!isNaN(loginDate.getTime())) {
        const expiryDate = new Date(loginDate.getTime() + expirySeconds * 1000);
        if (now > expiryDate) {
          localStorage.removeItem('token');
          localStorage.removeItem('calculatedExpiry');
          sessionStorage.removeItem('calculatedExpiry');
          router.navigate(['auth/login']);
          return false;
        }
        localStorage.setItem('calculatedExpiry', expiryDate.toISOString());
        sessionStorage.setItem('calculatedExpiry', expiryDate.toISOString());
        return true;
      }
    }
  }

  // Token is present and no expiration condition was broken
  return true;
};

const dynamicRouteParents: Record<string, string[]> = {
  '/permission': ['/administrator'],
  '/consumerdetail': ['/consumer_search', '/site_summary/consumer', '/dashboard/consumer_details', '/dashboard/consumer_search'],
  '/addsites': ['/configuration/site_configuration', '/site_summary/site', '/site_summary'],
  '/editsites': ['/configuration/site_configuration', '/site_summary/site', '/site_summary'],
  '/createbulkaction': ['/bulkaction'],
};

export const routePermissionGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const navServices = inject(NavmenuService);
  const rawRequestedUrl = state.url.split('?')[0].toLowerCase();
  let requestedUrl = rawRequestedUrl.startsWith('/') ? rawRequestedUrl : '/' + rawRequestedUrl;
  if (requestedUrl.length > 1 && requestedUrl.endsWith('/')) {
    requestedUrl = requestedUrl.slice(0, -1);
  }

  const normalizeRoute = (r: string) => {
    if (!r) return '';
    let clean = r.split('?')[0].toLowerCase().trim();
    if (!clean.startsWith('/') && !clean.startsWith('http')) clean = '/' + clean;
    if (clean.length > 1 && clean.endsWith('/')) clean = clean.slice(0, -1);
    return clean;
  };

  const bypassRoutes = [
    '/auth/login',
    '/auth/forgot-password',
    '/error-404',
    '/no-access',
    '/site_summary',
    '/sitesummary',
    '/dashboard/site_summary',
    '/dashboard/sitesummary',
    '/dashboard',
    '/consumerdetail',
    '/consumer_search',
    '/ev_charger'

  ];

  const isBypassed = (url: string) => {
    const norm = normalizeRoute(url);
    return bypassRoutes.some(bypass => norm === bypass || norm.startsWith(bypass + '/'));
  };

  if (isBypassed(requestedUrl)) {
    if (!requestedUrl.startsWith('/auth') && requestedUrl !== '/error-404' && requestedUrl !== '/no-access') {
      localStorage.setItem('lastActiveUrl', state.url);
    }
    return true;
  }

  // Check if requested URL is a dynamic child route whose parent is bypassed
  for (const prefix of Object.keys(dynamicRouteParents)) {
    if (requestedUrl === prefix || requestedUrl.startsWith(prefix + '/')) {
      const parentRoutes = dynamicRouteParents[prefix];
      if (parentRoutes.some(pr => isBypassed(pr))) {
        if (!requestedUrl.startsWith('/auth') && requestedUrl !== '/error-404' && requestedUrl !== '/no-access') {
          localStorage.setItem('lastActiveUrl', state.url);
        }
        return true;
      }
    }
  }

  const sidebarItemsRaw = localStorage.getItem('sidebarItems');
  const sideMenuListRaw = localStorage.getItem('sideMenuList');

  // If neither menu cache is populated yet (e.g. hard refresh before API response), allow navigation
  if (!sidebarItemsRaw && !sideMenuListRaw) {
    return true;
  }

  try {
    const allowedRoutes = new Set<string>();

    const addRouteVariants = (r: string) => {
      const norm = normalizeRoute(r);
      if (norm) {
        allowedRoutes.add(norm);
        // Also add dashboard / site_summary mirror aliases
        if (norm.startsWith('/site_summary')) {
          allowedRoutes.add(norm.replace('/site_summary', '/dashboard/site_summary'));
          allowedRoutes.add(norm.replace('/site_summary', '/dashboard'));
        } else if (norm.startsWith('/dashboard/site_summary')) {
          allowedRoutes.add(norm.replace('/dashboard/site_summary', '/site_summary'));
        } else if (norm.startsWith('/dashboard')) {
          allowedRoutes.add(norm.replace('/dashboard', '/site_summary'));
        }
      }
    };

    // 1. Traverse UI-filtered menu tree (sidebarItems)
    if (sidebarItemsRaw) {
      const items = JSON.parse(sidebarItemsRaw);
      const traverseUI = (node: any) => {
        if (!node) return;

        // Leaf items (or items with explicit config)
        if (node.path && node.path.route && (!node.children || node.children.length === 0 || node.type === 'link')) {
          addRouteVariants(node.path.route);
        }

        if (Array.isArray(node.config)) {
          node.config.forEach((cfg: any) => {
            if (cfg && cfg.url && cfg.is_view !== false) {
              addRouteVariants(cfg.url);
              if (node.path && node.path.route) {
                addRouteVariants(node.path.route);
              }
            }
          });
        }

        if (Array.isArray(node.children) && node.children.length > 0) {
          node.children.forEach(traverseUI);
        }
      };

      if (Array.isArray(items)) {
        items.forEach(traverseUI);
      }
    }

    // 2. Traverse raw role menu tree (sideMenuList)
    if (sideMenuListRaw) {
      const rawMenu = JSON.parse(sideMenuListRaw);
      const traverseRaw = (node: any, parentRoute: string = '') => {
        if (!node || typeof node !== 'object') return;
        for (const key in node) {
          if (['icon', 'url', 'api_url', 'category', 'is_view', 'is_add', 'is_edit', 'id'].includes(key)) {
            continue;
          }
          const val = node[key];
          if (val && typeof val === 'object') {
            const nodeUrl = (val.url && typeof val.url === 'string') ? val.url.trim() : '';
            const isView = val.is_view !== false;

            if (nodeUrl && isView) {
              const fullRoute = parentRoute ? `${parentRoute.replace(/\/+$/, '')}/${nodeUrl.replace(/^\/+/, '')}` : nodeUrl;
              addRouteVariants(fullRoute);
            }
            const nextParent = nodeUrl ? (parentRoute ? `${parentRoute.replace(/\/+$/, '')}/${nodeUrl.replace(/^\/+/, '')}` : nodeUrl) : parentRoute;
            traverseRaw(val, nextParent);
          }
        }
      };
      traverseRaw(rawMenu);
    }

    // 3. Add landing page / localRedirection
    const landingPage = localStorage.getItem('localRedirection');
    if (landingPage) {
      addRouteVariants(landingPage);
    }

    if (allowedRoutes.size === 0) {
      return true;
    }

    // Dynamic-segment / child action routes: check against allowed parent routes or bypass routes
    for (const prefix of Object.keys(dynamicRouteParents)) {
      if (requestedUrl === prefix || requestedUrl.startsWith(prefix + '/')) {
        const parentRoutes = dynamicRouteParents[prefix];
        const isParentAllowed = parentRoutes.some(pr => {
          const normPr = normalizeRoute(pr);
          return allowedRoutes.has(pr) || allowedRoutes.has(normPr) || isBypassed(normPr);
        });
        if (isParentAllowed) {
          localStorage.setItem('lastActiveUrl', state.url);
          return true;
        }
        console.warn(`Access denied for dynamic route: ${state.url} (missing parent in ${parentRoutes.join(', ')})`);
        navServices.noAccessTriggered$.next({ requestedUrl: state.url });
        return false;
      }
    }

    // Check exact match or allowed route
    const isAllowed = allowedRoutes.has(requestedUrl);

    if (isAllowed) {
      if (!requestedUrl.startsWith('/auth') && requestedUrl !== '/error-404' && requestedUrl !== '/no-access') {
        localStorage.setItem('lastActiveUrl', state.url);
      }
      return true;
    }

    console.warn(`Access denied for route: ${state.url}`);
    navServices.noAccessTriggered$.next({ requestedUrl: state.url });
    return false;
  } catch (e) {
    console.error('Failed to parse menu items in routePermissionGuard', e);
    return true;
  }
};

